import axiosInstance from '../fetchConfig/axiosConfig'
import {useCallback } from "react";
import {toast} from './toast';
import debounce from 'lodash/debounce';
import logger from '../utils/logger';

// Create a single instance of the debounced toast function
let debouncedToastInstance = null;
let copiedItems = new Set();
let copyTimeout = null;

const getDebouncedToast = () => {
  if (!debouncedToastInstance) {
    debouncedToastInstance = debounce((message) => {
      // Dismiss any existing toasts before showing the new one
      toast.dismiss();
      toast.success(message, { autoClose: 3000 });
    }, 300);
  }
  return debouncedToastInstance;
};

export const handleCopy = (text, type) => {
  if (!text) return;
  
  navigator.clipboard.writeText(text)
    .then(() => {
      // Add the copied item to our set
      copiedItems.add(type);
      
      // Clear any existing timeout
      if (copyTimeout) {
        clearTimeout(copyTimeout);
      }
      
      // Set a new timeout to show the toast after a short delay
      copyTimeout = setTimeout(() => {
        const items = Array.from(copiedItems);
        let message;
        
        if (items.length === 1) {
          message = `Copied ${items[0]} to clipboard!`;
        } else if (items.length === 2) {
          message = `Copied ${items[0]} and ${items[1]} to clipboard!`;
        } else {
          const lastItem = items.pop();
          message = `Copied ${items.join(', ')} and ${lastItem} to clipboard!`;
        }
        
        const toast = getDebouncedToast();
        toast(message);
        
        // Clear the set after showing the toast
        copiedItems.clear();
      }, 100); // Small delay to allow multiple rapid clicks to be captured
    })
    .catch(err => {
      logger.error('Failed to copy text: ', err);
    });
};

export function getDefaultYear() {
  const today = new Date();
  const currentYear = today.getFullYear();
  const aprilFirst = new Date(currentYear, 3, 1);

  return today >= aprilFirst ? currentYear : currentYear - 1;
}

/** Selectable tax years: upper bound is currentYear+1 on/after July 1, else currentYear; always back through 2023 (descending). */
export function getTaxYearOptions(restrictedYear) {
  const today = new Date();
  const currentYear = today.getFullYear();
  const upperBound = today >= new Date(currentYear, 6, 1) ? currentYear + 1 : currentYear;
  const options = [];
  for (let year = upperBound; year >= 2023; year--) {
    options.push({
      key: year.toString(),
      value: year,
      label: year.toString(),
      displayText: year.toString(),
      disabled: restrictedYear ? year !== parseInt(restrictedYear, 10) : false,
    });
  }
  return options;
}

export async function loadCourtDates(user = null, showSettledCourtdates = null) {
  try {
    const currentDate = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
    
    // If no user provided, fall back to localStorage for backward compatibility
    const userInfo = user || JSON.parse(localStorage.getItem('userInfo'));

    // Use showSettledCourtdates if provided, otherwise default to false
    const historicalUser = showSettledCourtdates !== null ? showSettledCourtdates : false;

    // Use the viewHistorical field to determine the date to use
    const dateToUse = userInfo?.viewHistorical ? 
      new Date(Date.now() - (90 * 24 * 60 * 60 * 1000)).toISOString().split('T')[0] : 
      currentDate;

    const courtResponse = await axiosInstance.get(`/load_court_dates?date=${dateToUse}&historicalUser=${historicalUser}`);
    
    // Handle the new structure with MuniCodes
    const courtDates = Object.keys(courtResponse.data);
    logger.log('📅 Court dates:', courtDates);
    
    // Sort dates chronologically (ascending order)
    const sortedCourtDates = courtDates.sort((a, b) => {
      const dateA = new Date(a);
      const dateB = new Date(b);
      return dateA - dateB; // Ascending order (earliest first)
    });
    
    const processedDates = sortedCourtDates.map((date) => {
      const courtDateData = courtResponse.data[date];
      
      // Handle both old format (string) and new format (object with MuniCodes)
      if (typeof courtDateData === 'string') {
        // Legacy format - just the date string
        return {
          label: date ? new Date(date).toLocaleDateString() : '',
          value: date,
          MuniCodes: []
        };
      } else if (typeof courtDateData === 'object' && courtDateData.MuniCodes) {
        // New format - object with date, MuniCodes, and MuniOptions
        // date is already a formatted date string (MM/DD/YYYY)
        return {
          label: courtDateData.date,
          value: courtDateData.date,  // Use formatted date string as value
          MuniCodes: courtDateData.MuniCodes,
          MuniOptions: courtDateData.MuniOptions
        };
      } else {
        // Fallback
        return {
          label: date ? new Date(date).toLocaleDateString() : '',
          value: date,
          MuniCodes: []
        };
      }
    });
    
    return processedDates;
  } catch (error) {
    logger.error('Error fetching court dates:', error);
    return [];
  }
}

// Absolutely insanely gross code. But this works for now. Hopefully you can just hire some poor soul who will fix this garbage
// at some point in the future. (My personal apologies if you are the one reading this - James)
export async function updateMuniObj({updateField, updateValue, updateString = '', navigate, urlString, courtMuni, nyDataObj, county = null, TaxYear=null, setCountyOptions}) {
const url = new URL(urlString, window.location.origin);
const searchParams = new URLSearchParams(url.search);
const currentValue = searchParams.get(updateField);
const courtDate = searchParams.get('CourtDate') // instead of removing from inputs (for laziness's sake just overwrote here)
county = searchParams.get('county') // instead of removing from inputs (for laziness's sake just overwrote here)
// Ensure that when running this for a court date, you also update the taxyear based on that specific courtdate.
// Requries passing in the taxYear to the function.
if (currentValue === updateValue) {
    return;
}
// Update the relevant search parameter
searchParams.set(updateField, updateValue);

if (updateField === 'county' || updateField ==='municipality' || updateField === 'village') {
    // This is where you set your munistring
    if (updateField === 'county') {
      searchParams.set('municipality', 'All');
      searchParams.set('village', 'All');
      searchParams.set('municipalityString', '');
      searchParams.set('villageString', '');
      searchParams.set('MuniCode', updateValue);
      searchParams.delete('municipality');
      // Find the key in nyDataObj that matches the selectVal with updateValue
      let countyString = '';
      for (const county in nyDataObj) {
          if (nyDataObj[county].selectVal === updateValue) {
              countyString = county; // Set countyString to the matching key
              break;
          }
      }
      // Set the countyString to the search parameters
      searchParams.set(updateField + 'String', countyString);
  } 
  else if (updateField === 'municipality') {
    const muniCode = county + updateValue;
    const countyString = searchParams.get('countyString');

    // If a courtDate is selected, update the tax year based on the courtDate's muni
    if (courtDate) {
        const courtMuniExists = courtMuni.find(court => court.muni === muniCode);
        if (courtMuniExists) {
            // Set the tax year to the matched value
            searchParams.set('TaxYear', courtMuniExists.TaxYear);
        }
    }

    // Find the key within the county's municipalities that matches updateValue
    let muniString = '';
    if (nyDataObj[countyString] && nyDataObj[countyString].municipalities) {
        for (const muniKey in nyDataObj[countyString].municipalities) {
            if (nyDataObj[countyString].municipalities[muniKey].selectVal === updateValue) {
                muniString = muniKey; // Set muniString to the matching key
                break;
            }
        }
    }

    // Set the MuniCode and county in search parameters
    searchParams.set('MuniCode', muniCode);
    searchParams.set('county', county);
    searchParams.set('municipalityString', muniString); // Set the extracted muniString
}else if (updateField === 'village') {
  const currentMuniCode = searchParams.get('MuniCode') || '';
  const newValues = updateValue.slice(-2); // Append these
  const countyString = searchParams.get('countyString');
  const muniString = searchParams.get('municipalityString');
  // the normal village handler:
  // Determine the length of the current MuniCode
  const currentLength = currentMuniCode.length;
  
  // Form the new MuniCode
  let updatedMuniCode;
  if (currentLength > 3) {
      updatedMuniCode = currentMuniCode.slice(0, -2) + newValues;
  } else {
      updatedMuniCode = currentMuniCode + newValues;
  }
  
  // Update the searchParams with the new MuniCode
  searchParams.set('MuniCode', updatedMuniCode);
  
  // Find the matching villageString within municipalities
  let villageString = '';
  if (nyDataObj[countyString] && nyDataObj[countyString].municipalities[muniString]) {
      const villageObj = nyDataObj[countyString].municipalities[muniString].villages;
      
      for (const villageKey in villageObj) {
          if (villageObj[villageKey].selectVal === updateValue) {
              villageString = villageKey; // Set villageString to the matching key
              break;
          }
      }
  }
  
  // Update searchParams with the extracted villageString
  searchParams.set('villageString', villageString);
}
} else if (updateField === 'CourtDate') {
        searchParams.delete('municipality')
        searchParams.delete('county')
        searchParams.delete('taxYear')
        if(updateValue === ''){ // set the default countyoptions if you remove the court date.
          setCountyOptions(
            [ // setting this here instead of @ higher level context for the moment.
              { value: "All", label: "All", selectVal: 'All'},
              ...Object.keys(nyDataObj).map(key => ({
                  label: key,
                  value: nyDataObj[key].selectVal
              }))
          ]
          )
        }
}

// Handler to clear out state if all selected
if (updateValue === 'All') {
  if (updateField === 'village') {
      searchParams.set('villageString', '');
      searchParams.set('village', 'All');
      searchParams.set('MuniCode', county + searchParams.get('municipality'));
  } else if (updateField === 'municipality') {
      searchParams.set('municipalityString', '');
      searchParams.set('municipality', 'All');
      searchParams.set('MuniCode', county);
  }
}

const newUrlString = `${url.pathname}?${searchParams.toString()}`;
navigate(newUrlString);
}


  export async function getCountyOptions({
      setMuniOptions,
      nyDataObj,
      courtMuni = [],
      navigate,
      setCountyOptions
  }) {
    // maybe break this out so you memoize the county options for the court date, and the muniOptions based on the court date you select
    const url = new URL(window.location);
    const searchParams = new URLSearchParams(url.search);
      
      // IF courtdate has a value, set your county options based on that. Then, based on county selection, set your available muni options.
      const countyOptions = [];  
      // its not clear to me that we need this if statement.
              courtMuni.forEach((court) => { // this properly iterates over this array to define the munioptions list.
                  let countyStringKey = court.muni.charAt(0);
                  
                  const courtCountyKey = Object.keys(nyDataObj).find(key => nyDataObj[key].selectVal === countyStringKey);
                  // Look for the countyStringKey in the countyOptions array of objects
                  const countyObjectExists = countyOptions.find(option => option.value === countyStringKey);
                  
                  if (!countyObjectExists) {
                    // logger.log(`Adding ${countyStringKey} to countyOptions`);
                    const countyObject = {
                      value: countyStringKey,
                      label: courtCountyKey
                    };
                    countyOptions.push(countyObject);
                  }
  
              })
              
              if(countyOptions.length<2){ // if only one county avail, set. If multiple, force user to select.
                // logger.log(countyOptions.length) // this should happen only if the user has multiple county to pick from.
                searchParams.set('county', countyOptions[0].value);
              }else{
                searchParams.set('county', 'select');
              }
            searchParams.set('municipality', 'All'); // force user to make a muni selection
            searchParams.set('MuniCode', '');
        if(courtMuni.length<2){ // when only one option, it sets the values as it should.
          const countyStringKey = courtMuni[0].muni.charAt(0);
            searchParams.set('MuniCode', courtMuni[0].muni);
            searchParams.set('TaxYear', courtMuni[0].TaxYear);
            searchParams.set('county', countyStringKey);
            searchParams.set('municipality', courtMuni[0].muni.substr(1, 2));
            // Set your munioptions here so it can update the muni options based on the court date.
            const matchingOptions = [];
            const courtCountyKey = Object.keys(nyDataObj).find(key => nyDataObj[key].selectVal === countyStringKey);
            
            // Lookup in the nyDataObject for the municipality based on the county + muni selection
            Object.keys(nyDataObj[courtCountyKey].municipalities).forEach((key) => {
                const municipality = nyDataObj[courtCountyKey].municipalities[key];
                if (municipality.selectVal === courtMuni[0].muni.substr(1, 2)) {
                  matchingOptions.push({
                    value: municipality.selectVal,
                    label: key,
                    selectVal: municipality.nameWithSelectVal,
                    county: countyStringKey,
                    TaxYear: courtMuni[0].TaxYear,
                  });
                }
            });
            setMuniOptions(matchingOptions);
          }
            // Instead of navigating and setting state here, you could return all these values and set them in the parent component.
            const newUrlString = `${url.pathname}?${searchParams.toString()}`;
            // logger.log(countyOptions)
            setCountyOptions(countyOptions);
            navigate(newUrlString);
  }


  export async function getMunicipalityOptions({
    setMuniOptions,
    nyDataObj,
    county,
    courtMuni = [],
    taxYear,
    navigate,
  }) {
  const url = new URL(window.location);
  const searchParams = new URLSearchParams(url.search);
  // logger.log(`county: ${county}`)
  // logger.log(`taxYear: ${taxYear}`)
  // logger.log(courtMuni)

  // Set your default options
  let countyKey = Object.keys(nyDataObj).find(key => nyDataObj[key].selectVal === county);
  if(county === 'All' || county === 'select'){
    return;
  }

    let muniOptions = [
      { value: "All", label: "All", selectVal: 'All'},
      ...Object.keys(nyDataObj[countyKey].municipalities).map((key) => ({
          value: nyDataObj[countyKey].municipalities[key].selectVal,
          label: key,
          selectVal: nyDataObj[countyKey].municipalities[key].nameWithSelectVal,
          TaxYear: taxYear
      }))
    ]
  if(courtMuni.length>1){ // when a court date run, set your options to only be what is scheduled, and set county drop.
            const matchingOptions = [];
            // IF courtdate has a value, set your county options based on that. Then, based on county selection, set your available muni options.
              courtMuni.forEach((court) => { // this properly iterates over this array to define the munioptions list.
                // If courtMuniCounty key is equal to the 'county' value, continue iterating. Otherwise, skip.
                if (court.muni.charAt(0) !== county) {
                  return;
                }
                  const muniSubstring = court.muni.substring(1, 3);
                  const countyStringKey = court.muni.charAt(0);
                  const courtCountyKey = Object.keys(nyDataObj).find(key => nyDataObj[key].selectVal === countyStringKey);
                  Object.keys(nyDataObj[courtCountyKey].municipalities).forEach((key) => {
                      const municipality = nyDataObj[courtCountyKey].municipalities[key];
                      if (municipality.selectVal === muniSubstring) {
                          matchingOptions.push({
                              value: municipality.selectVal,
                              label: key,
                              selectVal: municipality.nameWithSelectVal,
                              county: countyStringKey,
                              TaxYear: court.TaxYear,
                          });
                      }
                  });
              })
              muniOptions = matchingOptions
          }

          // If you only have one thing, set the value. otherwise tell user to pick a muni.
              if(muniOptions.length===1){
                // logger.log('only one thing!')
                searchParams.set('municipality', muniOptions[0].value);
                searchParams.set('MuniCode', muniOptions[0].county+muniOptions[0].value);
                searchParams.set('TaxYear', muniOptions[0].TaxYear);
                searchParams.set('county', muniOptions[0].county);
              }else{ // otherwise drive the user to select a muni.
                if(courtMuni.length>1){
                searchParams.set('municipality', 'select');
                }
              }
              // You are deleting the village param -- this is why it defaults to all. Fine for now until you clean this entire dumpsterfire up
              searchParams.delete('village');
            const newUrlString = `${url.pathname}?${searchParams.toString()}`;
              // Sort the muniOptions array ascending by municipality value
              muniOptions.sort((a, b) => a.value - b.value);
              setMuniOptions(muniOptions)
              navigate(newUrlString);
}

  export function getVillageOptions(setVillageOptions, nyDataObj, municipality, county) {
    if(Object.keys(nyDataObj).length >0){
        
        if (municipality !== 'All') {
            const countyKey = Object.keys(nyDataObj).find(key => nyDataObj[key].selectVal === county);
            const muniKey = Object.keys(nyDataObj[countyKey].municipalities).find(key => nyDataObj[countyKey].municipalities[key].selectVal === municipality);
            const villages = nyDataObj[countyKey]?.municipalities[muniKey]?.villages || null;
            if (villages) {
                setVillageOptions(
                    [
                        { value: "All", label: "All", selectVal: 'All' },
                        ...Object.keys(villages).map((key) => ({
                            value: villages[key].selectVal,
                            label: key,
                            selectVal: villages[key].nameWithSelectVal
                        }))
                    ]
                );
            } else {
                setVillageOptions([{ value: "All", label: "All", selectVal: 'All' }]);
            }
        }else{
          setVillageOptions([{ value: "All", label: "All", selectVal: 'All' }]);
        }
    }
  }

const useReviewedComps = (comp, getCourtComps, taxYear, getCompSheet, setCourtComps) => {
  const getReviewedComps = useCallback(async (courtComps) => { // this needs to handle the "null's"
    // logger.log('running helperfunction to get court comp data + inventory');
    try {
      // Construct compObject with subject and comps
      const compObject = {
        subject: courtComps.PID,
        comp1: courtComps.Comps[1],
        comp2: courtComps.Comps[2],
        comp3: courtComps.Comps[3],
        comp4: courtComps.Comps[4],
        comp5: courtComps.Comps[5],
      };
      // logger.log(compObject);

      const parcelIds = getCompSheet[comp]?.parcel_id || [];
      const compSheetIndexes = Object.values(compObject).map(compValue => {
        if (Number.isInteger(compValue)) {
          return null;
        }
        const index = parcelIds.indexOf(compValue);
        return index !== -1 ? index : null;
      });
      // logger.log(compSheetIndexes)

      let reviewedCompInventory = [];
      try {
        const reviewedCompInventoryResponse = await axiosInstance.post(`/load_comparables`, compObject);
        reviewedCompInventory = reviewedCompInventoryResponse.data;
        // logger.log(reviewedCompInventory)

        // Initialize the object for the current 'comp' index from getCourtComps or as an empty object
        const initialObject = getCourtComps[comp] || {};

        const keys = Object.keys(getCompSheet[comp] || {});
        keys.forEach(key => {
          const valueArray = getCompSheet[comp][key];
          // logger.log(valueArray)

          // Map over compSheetIndexes to construct the array of values for this key
          const values = compSheetIndexes.map((compSheetIndex,idx) => {
            if (compSheetIndex !== null) {
              return valueArray[compSheetIndex] || null;
            }
            // HERE you should set values per the reviewed comp inventory instead of null.

            // valueArray[compSheetIndex] = compObject[comp[idx]]
            return null;
          });

          // Assign the array of values to the corresponding key in initialObject
          if(key==='parcel_id'){
            // PUt in handling to render that form your compObj
          }
          initialObject[key] = values;

        });

        // Update the specific index in the array with initialObject
        const updatedCourtComps = [...getCourtComps];
        updatedCourtComps[comp] = initialObject;

        // logger.log('Final Object:', updatedCourtComps);
        setCourtComps(updatedCourtComps);

      } catch (error) {
        logger.error('Error fetching reviewedCompInventory:', error);
        return;
      }
      return reviewedCompInventory;

    } catch (error) {
      logger.error('Error fetching additional comps:', error);
    }
  }, [comp, getCourtComps, getCompSheet, setCourtComps]);

  return getReviewedComps;
};

export default useReviewedComps;

// Helper to format JS Date as MySQL DATETIME string (YYYY-MM-DD HH:MM:SS)
export function toMySQLDateTime(date) {
  const pad = n => n.toString().padStart(2, '0');
  return (
    date.getFullYear() + '-' +
    pad(date.getMonth() + 1) + '-' +
    pad(date.getDate()) + ' ' +
    pad(date.getHours()) + ':' +
    pad(date.getMinutes()) + ':' +
    pad(date.getSeconds())
  );
}